/*
 * Anchor resolution.
 *
 * A step points at a `data-tour-id`, never at a CSS class or DOM shape. That
 * attribute is the contract between whoever writes the tour copy and the
 * component: it survives restyling, and it is the single thing a dev has to
 * keep alive when refactoring.
 *
 * It is also how role targeting works. The app renders different controls for
 * an owner, a team member, an occupant and an FRS user, and gates whole
 * feature groups behind the subscription. Rather than re-encode all of that in
 * every tour, a step whose anchor is not in the DOM is simply skipped — if the
 * user cannot see the button, they do not get told about it.
 */
export const anchorSelector = id => `[data-tour-id="${id}"]`

const isUsable = (el, requireEnabled) => {
  if (!el || !el.isConnected) return false
  const rect = el.getBoundingClientRect()
  if (rect.width === 0 && rect.height === 0) return false
  const style = window.getComputedStyle(el)
  if (style.visibility === 'hidden' || style.display === 'none') return false
  /*
   * A step that offers to press the control for the user has to wait until
   * pressing it would do something. Sync Units, for one, stays disabled until
   * the map has resolved its UPRNs — highlighting it before then explains a
   * button the user cannot use and makes the CTA a no-op.
   */
  if (requireEnabled && (el.disabled || el.getAttribute('aria-disabled') === 'true')) {
    return false
  }
  return true
}

export const findAnchor = (id, requireEnabled = false) => {
  const matches = Array.from(document.querySelectorAll(anchorSelector(id)))
  return matches.find(el => isUsable(el, requireEnabled)) || matches[0] || null
}

/*
 * Resolves an anchor, waiting for it to turn up if it is not there yet — a
 * step can point at something inside a dialog that the previous step's CTA
 * only just opened, and that dialog may still be fetching.
 *
 * Resolves `null` on timeout rather than rejecting: a missing anchor is an
 * ordinary outcome (wrong role, feature locked, nothing to reconcile), and the
 * caller skips the step instead of failing the tour.
 *
 * `timeout: null` waits indefinitely. That is what the auto-start uses: the
 * first anchor of a tour may only exist after the user has done something —
 * picked a building on the map, say — which no fixed number of seconds can
 * predict. The returned promise carries a `cancel()` for the caller to stop
 * watching when the route changes.
 */
export const waitForAnchor = (id, timeout = 6000, requireEnabled = false) => {
  let settled = false
  let observer = null
  let timer = null
  let finish = () => {}

  const promise = new Promise(resolve => {
    finish = el => {
      if (settled) return
      settled = true
      if (observer) observer.disconnect()
      if (timer) clearTimeout(timer)
      resolve(el)
    }

    const existing = findAnchor(id, requireEnabled)
    if (isUsable(existing, requireEnabled)) return finish(existing)

    observer = new MutationObserver(() => {
      const el = findAnchor(id, requireEnabled)
      if (isUsable(el, requireEnabled)) finish(el)
    })
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'data-tour-id',
        'style',
        'class',
        'disabled',
        'aria-disabled'
      ]
    })

    if (timeout !== null) timer = setTimeout(() => finish(null), timeout)
  })

  promise.cancel = () => finish(null)
  return promise
}

const SCROLL_FALLBACK_MS = 350

const isFullyVisible = (el, bottomInset = 0) => {
  const rect = el.getBoundingClientRect()
  return rect.top >= 80 && rect.bottom <= window.innerHeight - Math.max(80, bottomInset)
}

export const scrollAnchorIntoView = (el, bottomInset = 0) => {
  if (!el || !el.isConnected) return () => {}
  if (isFullyVisible(el, bottomInset)) return () => {}
  el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
  const timer = setTimeout(() => {
    if (el.isConnected && !isFullyVisible(el, bottomInset)) el.scrollIntoView({ block: 'center', inline: 'nearest' })
  }, SCROLL_FALLBACK_MS)
  return () => clearTimeout(timer)
}
